import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Artifact, ArtifactAssemblyInfoDto, ArtifactType, AssemblyArtifact, AssemblyArtifactDto } from '../models/artifact';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { JsonHttpService } from 'src/app/shared/http/json-http.service';
import { GenericModalService, ModalMultiButton, ModalSettingsBase, ModalSettingsConfirmMultiButton, ModalSettingsMessage, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { DateTimeService } from '../../shared/datetime/datetime.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { DxDataGridModule, DxFileUploaderModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoFormItemModule, DxiButtonModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

import { AssemblyArtifactGridComponent } from './assembly-artifact-grid/assembly-artifact-grid.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { GridService } from '../../shared/grid/grid.service';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';

@Component({
    selector: 'app-developer-assembly-artifacts',
    templateUrl: './developer-assembly-artifacts.component.html',
    styleUrls: ['./developer-assembly-artifacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoFormItemModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxFileUploaderModule, DxoSelectionModule, AssemblyArtifactGridComponent]
})

export class DeveloperAssemblyArtifactsComponent implements OnInit, AfterViewInit {

    artifacts: AssemblyArtifactDto[] = [];
    assemblyInfos: ArtifactAssemblyInfoDto[] = [];
    selectedAssemblyInfo: ArtifactAssemblyInfoDto;
    allowAdding = true;
    currentUploadedArtifactDataGuid: string;
    multipleUploadAssemblies: AssemblyArtifactDto[] = [];

    @ViewChild('assemblyTypesTemplate', { static: true }) private assemblyTypesTemplate: TemplateRef<any>;
    @ViewChild('assemblyMultiUploadTemplate', { static: true }) private assemblyMultiUploadTemplate: TemplateRef<any>;

    constructor(
        private developerArtifactService: DeveloperArtifactService,
        private jsonHttpService: JsonHttpService,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        public dateTimeService: DateTimeService,
        public bizGridFiltervalues: BizGridFiltervalues,
        public gridService: GridService,
        private toastService: ToastService) {
        }

    async ngOnInit(): Promise<void> {
        await this.refresh();
    }
    ngAfterViewInit(): void {

    }

    async refresh() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifactsIncludingInternal();
    }

    allowUpdating(e) {
        return !e.row.data.IsSystemArtifact;
    }

    allowDeleting(e) {
        return !e.row.data.IsSystemArtifact;
    }

    allowShowLog(e) {
        return !e.row.data.IsSystemArtifact;
    }

    onInitNewRow(e: DxDataGridTypes.InitNewRowEvent<AssemblyArtifact>): void {
        e.data = {
            ArtifactId: null,
            Name: '',
            Description: '',
            ClassName: '',
            Filename: '',
            AssemblyType: '',
            UploadFileGuid: null,
            IsSystemArtifact: false
        } as AssemblyArtifact;
    }

    onRowInserting = (e: DxDataGridTypes.RowInsertingEvent<AssemblyArtifact>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.save(item));
    };

    onRowUpdating = (e: DxDataGridTypes.RowUpdatingEvent<AssemblyArtifact>): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData }, item => this.save(item), e.newData);
    };

    onRowRemoving = (e: DxDataGridTypes.RowRemovingEvent<AssemblyArtifact>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.deleteAssemblyArtifact(item));
    };

    onEditingStart(e: any): void {
        this.clearCurrentUploadedArtifactDataGuid();
    }
    onEditCanceled(e: any): void {
        this.clearCurrentUploadedArtifactDataGuid();
    }

    async save(artifact: AssemblyArtifact): Promise<AssemblyArtifact> {
        try {
            artifact.UploadFileGuid = this.currentUploadedArtifactDataGuid;
            const newArtifact = await this.developerArtifactService.saveAssemblyArtifact(artifact);
            this.toastService.showToast('Artifact saved...', ToastType.success);
            return newArtifact;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save artifact...', ToastType.error);
            return undefined;
        }
    }

    async deleteAssemblyArtifact(assemblyArtifact: AssemblyArtifact): Promise<AssemblyArtifact> {
        try {
            await this.developerArtifactService.deleteAssemblyArtifact(assemblyArtifact.ArtifactId, err => err?.status !== 409);
            this.toastService.showToast('Assembly artifact deleted...', ToastType.success);
        }
        catch (err) {
            // Process 409 error here; other errors are processed using generic error handling
            if (err?.status === 409) {
                const settings = new ModalSettingsConfirmMultiButton('Delete failed', `Error details: <pre style='white-space: pre-wrap;'>${err.error}</pre>`);
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.buttons = [new ModalMultiButton('Close', null, 'btn btn-primary')];
                await this.modalService.confirmMultiButton(settings);
            }
        }
        return assemblyArtifact;
    }

    async onRowValidating(e: any): Promise<void> {
        const oldRow = e.oldData as AssemblyArtifact;
        // If this is a new artifact being created, e.oldData will be undefined.
        if (oldRow === undefined) {
            const newRow = e.newData as AssemblyArtifact;
            if (!this.isAssemblyNameUnique(newRow)) {
                e.isValid = false;
                e.errorText = 'Another artifact with same full assembly-name already exists.';
            }
            else {
                e.isValid = true;
            }
        }
        else if (!this.isAssemblyNameUnique(oldRow)) {
            e.isValid = false;
            e.errorText = 'Another artifact with same full assembly-name already exists.';
        }
        else if (!this.isAssemblyTypeTheSame(oldRow)) {
            e.isValid = false;
            e.errorText = `The uploaded assembly does not contain an implementation of the ${oldRow.AssemblyType} plugin type. Cannot update this artifact with the uploaded assembly.`;
        }
        else {
            e.isValid = true;
        }
    }

    async downloadArtifactData(artifact: Artifact) {
        await this.developerArtifactService.downloadArtifact(artifact.ArtifactId, ArtifactType.Assembly);
    }

    getUploadUrl() {
        return this.jsonHttpService.generateBackendUrl(`/api/developer/artifacts/upload-artifact-data`);
    }

    fileOnValueChanged(args: any, cellInfo: any): void {
        if (args.value.length > 0) {
            cellInfo.setValue(args.value[0].name);
        }
        else {
            cellInfo.setValue(null);
        }
    }

    fileOnBeforeSend(args: any, cellInfo: any): void {
        const request = args.request as XMLHttpRequest;
        const headers = this.jsonHttpService.generateHeaders();
        request.setRequestHeader('Authorization', headers.get('Authorization'));
    }

    fileOnUploaded(args: any, cellInfo: any) {
        if (args.request.status === 200) {
            const uploadGuid = JSON.parse(args.request.responseText);
            this.currentUploadedArtifactDataGuid = uploadGuid;

            setTimeout(() => {
                this.getTypesForUploadedAssembly(cellInfo);
            });
        }
    }

    isAssemblyNameUnique(aa: AssemblyArtifact): boolean {
        if (this.assemblyInfos.length > 0) {
            if (this.artifacts.some(a => a.AssemblyName === this.assemblyInfos[0].AssemblyName && a.ArtifactId !== aa.ArtifactId)) {
                return false;
            }
        }
        return true;
    }

    isAssemblyTypeTheSame(aa: AssemblyArtifact): boolean {
        if (this.assemblyInfos.length > 0 && aa.AssemblyType.length > 0) {
            if (!this.assemblyInfos.some(a => a.AssemblyType === aa.AssemblyType)) {
                return false;
            }
        }
        return true;
    }

    async getTypesForUploadedAssembly(cellInfo: any) {
        this.assemblyInfos = await this.developerArtifactService.resolveAssemblyArtifactTypes(this.currentUploadedArtifactDataGuid);

        var settings = {} as ModalSettingsBase;
        settings.size = 'lg';
        settings.backdrop = 'static';
        // Make sure we are on top with this modal!
        settings.windowClass = 'ngb-modal-on-top';
        settings.backdropClass = 'ngb-modal-on-top-backdrop';

        if (!this.isAssemblyNameUnique(cellInfo.data as AssemblyArtifact)) {
            var messageSettings = new ModalSettingsMessage();
            Object.assign(messageSettings, settings);
            messageSettings.body = 'Another artifact with same full assembly-name already exists.';
            messageSettings.title = 'Assembly already exists';
            this.modalService.message(messageSettings);
            return;
        }

        if (!this.isAssemblyTypeTheSame(cellInfo.data as AssemblyArtifact)) {
            const currentType = cellInfo.component.cellValue(cellInfo.row.rowIndex, 'AssemblyType');
            var messageSettings = new ModalSettingsMessage();
            Object.assign(messageSettings, settings);
            messageSettings.body = `The uploaded assembly does not contain an implementation of the ${currentType} plugin type. Cannot update this artifact with the uploaded assembly.`;
            messageSettings.title = 'Wrong assembly type';
            this.modalService.message(messageSettings);
            return;
        }

        if (this.assemblyInfos.length > 1) {
            try {
                var templateSettings = new ModalSettingsTemplate();
                Object.assign(templateSettings, settings);
                await this.modalService.openTemplate(this.assemblyTypesTemplate, templateSettings);
                this.setAssemblyInfo(this.selectedAssemblyInfo, cellInfo);
            } catch { } // Escape or close button hitted
        }
        else if (this.assemblyInfos.length === 1) {
            this.setAssemblyInfo(this.assemblyInfos[0], cellInfo);
        }
        else {
            var messageSettings = new ModalSettingsMessage();
            Object.assign(messageSettings, settings);
            messageSettings.body = 'No valid artifact-types found in uploaded file.';
            messageSettings.title = 'No valid types';
            this.modalService.message(messageSettings);
        }
    }

    async uploadMultiple() {
        await this.modalService.fileSelectorDirect('Select files', 'Select or drop the dll-files containing Link Artifacts.', true, 'Upload', 'Cancel', '.dll').then(async files => {
            if (!files.every(f => f.name.endsWith('.dll'))) {
                this.modalService.messageDirect('Invalid files', 'Only dll-files are allowed.');
                return;
            }

            this.loadingPanelService.showLoadingPanelDirect('Checking artifacts...');
            const fileGuids = await this.developerArtifactService.uploadArtifacts(files);
            this.multipleUploadAssemblies = await this.developerArtifactService.resolveMultipleAssemblyArtifactTypes(fileGuids);
            this.loadingPanelService.hideLoadingPanel();

            if (this.multipleUploadAssemblies.length > 0) {
                const settings = new ModalSettingsTemplate();
                settings.size = 'lg';
                settings.backdrop = 'static';
                // Make sure we are on top with this modal!
                settings.windowClass = 'hugeModal';
                settings.backdropClass = 'ngb-modal-on-top-backdrop';
                this.modalService.openTemplate(this.assemblyMultiUploadTemplate, settings).then(() => {
                    this.loadingPanelService.showLoadingPanelDirect('Saving artifacts...');
                    this.developerArtifactService.saveAssemblyArtifacts(this.multipleUploadAssemblies).then(() => {
                        this.loadingPanelService.hideLoadingPanel();
                        this.toastService.showToast('Artifacts saved successfully', ToastType.success);
                        this.refresh();
                    });
                }).catch(e => { });
            }
        }).catch(e => { });
    }

    calculateIsNewColumnValue(rowData: AssemblyArtifact) {
        return !rowData.ArtifactId;
    }

    setAssemblyInfo(assemblyInfo: ArtifactAssemblyInfoDto, cellInfo: any) {
        cellInfo.component.cellValue(cellInfo.row.rowIndex, "FileName", cellInfo.data.Filename);
        cellInfo.component.cellValue(cellInfo.row.rowIndex, "ClassName", assemblyInfo.ClassName);
        cellInfo.component.cellValue(cellInfo.row.rowIndex, "AssemblyName", assemblyInfo.AssemblyName);
        cellInfo.component.cellValue(cellInfo.row.rowIndex, "AssemblyType", assemblyInfo.AssemblyType);
        cellInfo.component.cellValue(cellInfo.row.rowIndex, "AssemblyTypeDisplayName", assemblyInfo.AssemblyTypeDisplayName);

        if (typeof cellInfo.data?.Name == 'undefined') { // Only overwrite name if no previous name was given
            cellInfo.component.cellValue(cellInfo.row.rowIndex, 'Name', assemblyInfo.Name);
        }

        if (typeof cellInfo.data?.Description == 'undefined') { // Only overwrite description if no previous description was given
            cellInfo.component.cellValue(cellInfo.row.rowIndex, 'Description', assemblyInfo.Description);
        }
    }

    selectedAssemblyInfoChanged(e: any) {
        if (e.selectedRowsData.length > 0) {
            this.selectedAssemblyInfo = e.selectedRowsData[0];
        }
    }

    getFilenameDisplayName(rowData: any) {
        return rowData.Filename; // This method is necessary for sorting/filtering/etc. when using cell-templates.
    }

    customizeItem(item: any) {
        if (item.dataField === 'DownloadDataField') { // Hide the 'Download' column when editing...
            item.visible = false;
        }
    }

    clearCurrentUploadedArtifactDataGuid(){
        this.currentUploadedArtifactDataGuid = null;
    }
}
